import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './CurrentDateDetails.css';
import DropdownMenu from '../../../../../components/DropdownMenu/DropdownMenu';
import type { DropdownMenuItem } from '../../../../../components/DropdownMenu/DropdownMenu';
import { SlotType, type CalDateInfo, type SlotInfo } from '../../../../../types/schedule.types';
import type { SceneInfo } from '../../../../../types/scene.types';
import type { ElementSceneInfo } from '../../../../../types/element.types';
import { ElementType } from '../../../../../types/element.types';
import {
  compareCastItemsByNO,
  compareExportNamesDigitLetterPinyin,
  getElementTypeDisplayOrder
} from '../../../../../utils/elementSortUtils';
import { useDataCache } from '../../../../../contexts/BreakdownDataCacheContext';
import { enrichElementRelations } from '../../../../../utils/enrichElementRelations';
import { ReactComponent as ArrowFrontIcon } from '../../../../../assets/svg/Schedule/arrow-front.svg';
import { ReactComponent as ArrowAfterIcon } from '../../../../../assets/svg/Schedule/arrow-after.svg';
import { ReactComponent as CastItemSvgIcon } from '../../../../../assets/svg/Breakdown/Elements/cast.svg';
import { ReactComponent as FeaturedRoleIcon } from '../../../../../assets/svg/Breakdown/Elements/featured.svg';
import { ReactComponent as ExtrasStandInActorIcon } from '../../../../../assets/svg/Breakdown/Elements/background.svg';
import { ReactComponent as ActionActorIcon } from '../../../../../assets/svg/Breakdown/Elements/stunt_players.svg';
import { ReactComponent as AnimalIcon } from '../../../../../assets/svg/Breakdown/Elements/animals.svg';
import { ReactComponent as VehicleIcon } from '../../../../../assets/svg/Breakdown/Elements/vehicles.svg';
import { ReactComponent as ArtPropIcon } from '../../../../../assets/svg/Breakdown/Elements/art_props.svg';
import { ReactComponent as CostumeIcon } from '../../../../../assets/svg/Breakdown/Elements/wardrobe.svg';
import { ReactComponent as MakeupIcon } from '../../../../../assets/svg/Breakdown/Elements/hair_makeup.svg';
import { ReactComponent as SfxMuIcon } from '../../../../../assets/svg/Breakdown/Elements/sfx_mu.svg';
import { ReactComponent as CameraIcon } from '../../../../../assets/svg/Breakdown/Elements/camera.svg';
import { ReactComponent as LightingIcon } from '../../../../../assets/svg/Breakdown/Elements/lighting.svg';
import { ReactComponent as PracticalEffectsIcon } from '../../../../../assets/svg/Breakdown/Elements/sfx.svg';
import { ReactComponent as VfxIcon } from '../../../../../assets/svg/Breakdown/Elements/vfx.svg';
import { ReactComponent as SoundIcon } from '../../../../../assets/svg/Breakdown/Elements/sound.svg';
import { ReactComponent as ExternalStaffIcon } from '../../../../../assets/svg/Breakdown/Elements/addl_labor.svg';
import { ReactComponent as SpecialEquipmentIcon } from '../../../../../assets/svg/Breakdown/Elements/special_equipment.svg';
import { ReactComponent as OtherIcon } from '../../../../../assets/svg/Breakdown/Elements/misc.svg';
import { ReactComponent as NoteIcon } from '../../../../../assets/svg/Breakdown/Elements/note.svg';
import { useScheduleSceneRows, CurrentDateDetailRowsList } from '../Components/sceneCards';
import { useShootingPeriod } from '../../Calendar/hooks/useShootingPeriod';

export type CalDateShiftUpsertPayload = {
  calDateDt: string;
  calDateBelongScheduleID: string;
  calDateShift?: string;
};

export interface CurrentDateDetailsProps {
  currentDate: Date;
  onDateSelect?: (date: Date) => void;
  calDates: CalDateInfo[];
  scheduleID?: string;
  upsertCalDate: (scheduleID: string, payload: CalDateShiftUpsertPayload) => Promise<unknown>;
  onScheduleRowsRefresh?: () => void;
  /** 与左侧日历同源，用于拍摄日序号/总天数（避免仅依赖 DayInfo 回调导致滞后或不一致） */
  slots?: SlotInfo[];
  /** 与 Calendar/DayInfo 一致：从分场缓存取场次页数，用于当日总页数实时汇总 */
  getSceneInfo?: (sceneID: string | undefined) => SceneInfo | undefined;
}

const elementTypeSvgIcons: Record<ElementType, React.FC<React.SVGProps<SVGSVGElement>>> = {
  [ElementType.CAST]: CastItemSvgIcon,
  [ElementType.FEATURED]: FeaturedRoleIcon,
  [ElementType.BACKGROUND]: ExtrasStandInActorIcon,
  [ElementType.STUNT_PLAYERS]: ActionActorIcon,
  [ElementType.ANIMALS]: AnimalIcon,
  [ElementType.VEHICLES]: VehicleIcon,
  [ElementType.ART_PROPS]: ArtPropIcon,
  [ElementType.WARDROBE]: CostumeIcon,
  [ElementType.HAIR_MAKEUP]: MakeupIcon,
  [ElementType.SFX_MU]: SfxMuIcon,
  [ElementType.CAMERA]: CameraIcon,
  [ElementType.LIGHTING]: LightingIcon,
  [ElementType.SFX]: PracticalEffectsIcon,
  [ElementType.VFX]: VfxIcon,
  [ElementType.SOUND]: SoundIcon,
  [ElementType.ADDL_LABOR]: ExternalStaffIcon,
  [ElementType.SPECIAL_EQUIPMENT]: SpecialEquipmentIcon,
  [ElementType.MISC]: OtherIcon,
  [ElementType.NOTE]: NoteIcon,
};

const compareRelations = (a: ElementSceneInfo, b: ElementSceneInfo): number => {
  const typeOrderA = getElementTypeDisplayOrder(a.elementType);
  const typeOrderB = getElementTypeDisplayOrder(b.elementType);

  if (typeOrderA !== typeOrderB) {
    return typeOrderA - typeOrderB;
  }

  if (a.elementType === ElementType.CAST && b.elementType === ElementType.CAST) {
    return compareCastItemsByNO(a, b);
  }

  const nameA = a.elementName || '';
  const nameB = b.elementName || '';
  return compareExportNamesDigitLetterPinyin(nameA, nameB);
};

function mergeTodayElementRelations(
  elementRelations: ElementSceneInfo[],
  todaySceneIds: Set<string>,
): ElementSceneInfo[] {
  const filtered = elementRelations.filter((rel) => todaySceneIds.has(rel.sceneID));
  const groupedByElementID = new Map<string, ElementSceneInfo>();

  filtered.forEach((rel) => {
    const elementID = rel.elementID;
    const currentQty = rel.elementQtyInScene || 1;

    if (!groupedByElementID.has(elementID)) {
      groupedByElementID.set(elementID, { ...rel });
    } else {
      const existing = groupedByElementID.get(elementID)!;
      const existingQty = existing.elementQtyInScene || 1;

      if (currentQty > existingQty) {
        groupedByElementID.set(elementID, { ...rel });
      }
    }
  });

  return Array.from(groupedByElementID.values());
}

function buildColumnMajorRows<T>(items: T[], columns: number, minRows: number): T[][] {
  const rowCount = Math.max(minRows, Math.ceil(items.length / columns));
  const rows: T[][] = Array.from({ length: rowCount }, () => []);
  for (let rowIdx = 0; rowIdx < rowCount; rowIdx += 1) {
    for (let colIdx = 0; colIdx < columns; colIdx += 1) {
      const flatIdx = colIdx * rowCount + rowIdx;
      rows[rowIdx].push(items[flatIdx] as T);
    }
  }
  return rows;
}

/** 该日详情底部「场次元素」：所有类型合并为统一列表，按列优先填充（先纵后横） */
const CurrentDateDetailsElements: React.FC<{
  elementRelations: ElementSceneInfo[];
  todaySceneIds: Set<string>;
  breakdownID?: string;
}> = ({ elementRelations: propsElementRelations, todaySceneIds, breakdownID }) => {
  const { t } = useTranslation();
  const { getElementRelationCache, subscribeElementRelationCache, getElementCache, subscribeElementCache } =
    useDataCache();
  const [enrichedRelations, setEnrichedRelations] = useState<ElementSceneInfo[]>(propsElementRelations || []);

  useEffect(() => {
    if (!breakdownID) {
      setEnrichedRelations(propsElementRelations || []);
      return;
    }

    const updateRelations = () => {
      const relationCache = getElementRelationCache(breakdownID);
      const itemCache = getElementCache(breakdownID);
      if (relationCache?.elementRelations && itemCache?.elements) {
        const enriched = enrichElementRelations(relationCache.elementRelations, itemCache.elements);
        setEnrichedRelations(enriched);
      } else {
        setEnrichedRelations(propsElementRelations || []);
      }
    };

    updateRelations();
    const unsubscribeRelation = subscribeElementRelationCache(breakdownID, updateRelations);
    const unsubscribeItem = subscribeElementCache(breakdownID, updateRelations);

    return () => {
      unsubscribeRelation();
      unsubscribeItem();
    };
  }, [
    breakdownID,
    propsElementRelations,
    getElementRelationCache,
    getElementCache,
    subscribeElementRelationCache,
    subscribeElementCache,
  ]);

  const elementRelations = enrichedRelations;

  const todayMergedAll = useMemo(() => {
    const merged = mergeTodayElementRelations(elementRelations, todaySceneIds);
    return merged.filter((rel) => !!rel.elementType);
  }, [elementRelations, todaySceneIds]);

  const sortedGroups = useMemo(() => {
    if (todayMergedAll.length === 0) return [];
    const sorted = [...todayMergedAll].sort(compareRelations);
    const groups: { categoryType: ElementType; items: ElementSceneInfo[] }[] = [];
    let previousType: ElementType | undefined;
    sorted.forEach((item) => {
      const ty = item.elementType!;
      if (ty !== previousType) {
        groups.push({ categoryType: ty, items: [item] });
        previousType = ty;
      } else {
        groups[groups.length - 1].items.push(item);
      }
    });
    return groups;
  }, [todayMergedAll]);

  const flattenedCells = useMemo(() => {
    type CellData =
      | { kind: 'category'; categoryType: ElementType }
      | { kind: 'item'; relation: ElementSceneInfo };
    const cells: CellData[] = [];
    sortedGroups.forEach((group) => {
      cells.push({ kind: 'category', categoryType: group.categoryType });
      group.items.forEach((relation) => {
        cells.push({ kind: 'item', relation });
      });
    });
    return cells;
  }, [sortedGroups]);

  const tableRows = useMemo(() => buildColumnMajorRows(flattenedCells, 4, 20), [flattenedCells]);
  const categoryTotals = useMemo(() => {
    const totals = new Map<ElementType, number>();
    sortedGroups.forEach((group) => {
      const total = group.items.reduce((sum, item) => sum + (item.elementQtyInScene || 1), 0);
      totals.set(group.categoryType, total);
    });
    return totals;
  }, [sortedGroups]);

  const featuredCrowdSummaryTypes: ElementType[] = [ElementType.FEATURED, ElementType.BACKGROUND];

  const renderFeaturedCrowdSummary = () => (
    <div className="scheduleDetails-today-Info scheduleDetails-today-Info--sceneRolesSummary">
      <div className="scheduleDetails-today-Info-left scheduleDetails-today-Info-left--roleSummary">
        {featuredCrowdSummaryTypes.map((ty, idx) => (
          <React.Fragment key={ty}>
            {idx > 0 ? t('common.pipeSeparator') : null}
            {t(`elementType.${ty}`)} {categoryTotals.get(ty) ?? 0}
          </React.Fragment>
        ))}
      </div>
    </div>
  );

  if (todayMergedAll.length === 0) {
    return (
      <>
        {renderFeaturedCrowdSummary()}
        <div className="scheduleDetails-today-elementList">
          <div className="today-elements-list-empty">{t('schedule.noElementsForDay')}</div>
        </div>
      </>
    );
  }

  if (sortedGroups.length === 0) {
    return (
      <>
        {renderFeaturedCrowdSummary()}
        <div className="scheduleDetails-today-elementList">
          <div className="today-elements-list-empty">{t('schedule.noOtherElementsForDay')}</div>
        </div>
      </>
    );
  }

  const renderCell = (
    cell:
      | { kind: 'category'; categoryType: ElementType }
      | { kind: 'item'; relation: ElementSceneInfo }
      | undefined,
  ) => {
    if (!cell) return null;

    if (cell.kind === 'category') {
      const IconComponent = elementTypeSvgIcons[cell.categoryType];
      return (
        <div className="today-element-category-header today-element-category-header--unifiedCell">
          {React.createElement(IconComponent, {
            className: 'today-element-category-icon',
            'aria-label': t(`elementType.${cell.categoryType}`),
          })}
          <span className="today-element-category-name">{t(`elementType.${cell.categoryType}`)}</span>
        </div>
      );
    }

    const maxQty = cell.relation.elementQtyInScene || 1;
    const showQtyTypes = new Set<ElementType>([
      ElementType.FEATURED,
      ElementType.BACKGROUND,
      ElementType.STUNT_PLAYERS,
      ElementType.ANIMALS,
      ElementType.VEHICLES,
    ]);
    const shouldShowQty = !!cell.relation.elementType && showQtyTypes.has(cell.relation.elementType);
    return (
      <div className="today-day-role-td-item">
        <span className="today-day-role-td-item-name">{cell.relation.elementName || ''}</span>
        {shouldShowQty ? <span className="today-day-role-td-item-qty">*{maxQty}</span> : null}
      </div>
    );
  };

  const tableEl = (
    <table className="today-elements-unified-table">
      <tbody>
        {tableRows.map((row, rowIdx) => (
          <tr key={`row-${rowIdx}`}>
            {row.map((cell, colIdx) => (
              <td
                key={`cell-${rowIdx}-${colIdx}`}
                className={`today-elements-unified-item-cell${cell?.kind === 'category' ? ' today-elements-unified-item-cell--category' : ''}${!cell ? ' today-elements-unified-item-cell--empty' : ''}`}
              >
                {renderCell(cell)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <>
      {renderFeaturedCrowdSummary()}
      <div className="scheduleDetails-today-elementList">{tableEl}</div>
    </>
  );
};

const CurrentDateDetails: React.FC<CurrentDateDetailsProps> = ({
  currentDate,
  onDateSelect,
  calDates,
  scheduleID,
  upsertCalDate,
  onScheduleRowsRefresh,
  slots = [],
  getSceneInfo,
}) => {
  const { t, i18n } = useTranslation();
  const { elementRelations, todaySceneIds, breakdownID } = useScheduleSceneRows();
  const [shiftEditingState, setShiftEditingState] = useState<{ value: string; isEditing: boolean } | null>(null);
  const shiftInputRef = useRef<HTMLInputElement>(null);
  const [shiftDropdownVisible, setShiftDropdownVisible] = useState(false);
  const [shiftDropdownPosition, setShiftDropdownPosition] = useState<{ top: number; left: number } | null>(null);

  const shiftMenuItems: DropdownMenuItem[] = [
    { id: '日班', label: t('schedule.shiftDay') },
    { id: '中班', label: t('schedule.shiftMiddle') },
    { id: '夜班', label: t('schedule.shiftNight') },
  ];

  useEffect(() => {
    if (!shiftDropdownVisible) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const isShiftInput =
        target.classList.contains('scheduleDetails-today-shift') ||
        (target.tagName === 'INPUT' && target.classList.contains('scheduleDetails-today-shift'));
      const isDropdownMenu = target.closest('.dropdown-menu');
      if (!isShiftInput && !isDropdownMenu) {
        setShiftDropdownVisible(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [shiftDropdownVisible]);

  const handlePreviousDay = () => {
    if (!onDateSelect) return;
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 1);
    onDateSelect(newDate);
  };

  const handleNextDay = () => {
    if (!onDateSelect) return;
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 1);
    onDateSelect(newDate);
  };

  const currentDateDisplayLabel = useMemo(() => {
    const y = currentDate.getFullYear();
    const m = currentDate.getMonth() + 1;
    const d = currentDate.getDate();
    if ((i18n.language || '').toLowerCase().startsWith('en')) {
      return `${y} / ${String(m).padStart(2, '0')} / ${String(d).padStart(2, '0')}`;
    }
    return t('schedule.yearMonthDayFormat', { year: y, month: m, day: d });
  }, [currentDate, i18n.language, t]);

  const { shootingPeriod, getShootingDayNumber } = useShootingPeriod(slots);
  const shootingDayProgressLabels = useMemo(() => {
    const n = getShootingDayNumber(currentDate);
    const total = shootingPeriod?.totalDays ?? null;
    return {
      current: n ?? '-',
      total: total ?? '-',
    };
  }, [currentDate, getShootingDayNumber, shootingPeriod]);

  const currentDateStrForSlots = useMemo(
    () =>
      `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`,
    [currentDate],
  );

  /** 与 DayInfo totalPages 同源：当日拍摄类 slot（SlotType.SHOOT）涉及场次的 scenePagesNumber 之和 */
  const totalPagesForCurrentDate = useMemo(() => {
    const daySlots = slots.filter((slot) => {
      if (!slot.slotDate) return false;
      const slotDateStr = slot.slotDate.split('T')[0];
      return slotDateStr === currentDateStrForSlots;
    });
    const daySoltSlots = daySlots.filter(
      (daySlot) => daySlot.slotType === SlotType.SHOOT || !daySlot.slotType,
    );
    const sceneIDs = daySoltSlots
      .map((slot) => slot.slotBelongSceneID)
      .filter((id): id is string => !!id);
    const uniqueSceneIDs = Array.from(new Set(sceneIDs));
    let total = 0;
    uniqueSceneIDs.forEach((sceneID) => {
      const scene = getSceneInfo?.(sceneID);
      if (scene && scene.scenePagesNumber !== undefined && scene.scenePagesNumber !== null) {
        const pages = Number(scene.scenePagesNumber);
        if (!isNaN(pages)) total += pages;
      }
    });
    return total;
  }, [slots, currentDateStrForSlots, getSceneInfo]);

  const totalPagesDisplay = totalPagesForCurrentDate
    ? totalPagesForCurrentDate.toFixed(1).replace(/\.0$/, '')
    : '0';

  return (
    <div className="scheduleDetails-tab-content">
      <div className="scheduleDetails-today-container-date-select">
        <div className="scheduleDetails-date-select-left">
          <button className="scheduleDetails-date-nav-btn" onClick={handlePreviousDay} title={t('schedule.prevDay')}>
            <ArrowFrontIcon className="scheduleDetails-date-nav-icon" aria-label={t('schedule.prevDay')} />
          </button>
          <span className="scheduleDetails-date-display">{currentDateDisplayLabel}</span>
          <button className="scheduleDetails-date-nav-btn" onClick={handleNextDay} title={t('schedule.nextDay')}>
            <ArrowAfterIcon className="scheduleDetails-date-nav-icon" aria-label={t('schedule.nextDay')} />
          </button>
        </div>
      </div>
      <div className="scheduleDetails-today-Info">
        <div className="scheduleDetails-today-Info-left">
          {t('schedule.shootDayProgress', {
            current: shootingDayProgressLabels.current,
            total: shootingDayProgressLabels.total,
          })}
        </div>
        <div className="scheduleDetails-today-Info-center">
          {(() => {
            const currentDateStrInner = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
            const todayCalDate = calDates.find((pd) => {
              if (!pd.calDateDt) return false;
              const calDateStr = pd.calDateDt.split('T')[0];
              return calDateStr === currentDateStrInner;
            });

            const editingState = shiftEditingState;
            const isEditing = editingState?.isEditing || false;
            const inputValue = isEditing && editingState ? editingState.value : (todayCalDate?.calDateShift || '');

            return (
              <>
                <input
                  ref={shiftInputRef}
                  type="text"
                  className={`scheduleDetails-today-shift ${isEditing ? 'scheduleDetails-today-shift-editing' : ''}`}
                  value={inputValue}
                  placeholder={t('schedule.shiftPlaceholder')}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (shiftInputRef.current) {
                      const rect = shiftInputRef.current.getBoundingClientRect();
                      setShiftDropdownPosition({ top: rect.bottom + 4, left: rect.left });
                      setShiftDropdownVisible(true);
                    }
                  }}
                  onFocus={() => {
                    const currentValue = todayCalDate?.calDateShift || '';
                    setShiftEditingState({ value: currentValue, isEditing: true });
                    if (shiftInputRef.current) {
                      const rect = shiftInputRef.current.getBoundingClientRect();
                      setShiftDropdownPosition({ top: rect.bottom + 4, left: rect.left });
                      setShiftDropdownVisible(true);
                    }
                  }}
                  onChange={(e) => {
                    setShiftEditingState({ value: e.target.value, isEditing: true });
                  }}
                  onBlur={() => {
                    if (!shiftEditingState || !scheduleID) return;
                    setTimeout(async () => {
                      const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
                      const newValue = shiftEditingState.value.trim();
                      try {
                        await upsertCalDate(scheduleID, {
                          calDateDt: dateStr,
                          calDateBelongScheduleID: scheduleID,
                          calDateShift: newValue || undefined,
                        });
                        onScheduleRowsRefresh?.();
                        setShiftEditingState(null);
                        setShiftDropdownVisible(false);
                      } catch (error) {
                        console.error('更新计划日排班失败:', error);
                        setShiftEditingState(null);
                        setShiftDropdownVisible(false);
                      }
                    }, 200);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      shiftInputRef.current?.blur();
                    } else if (e.key === 'Escape') {
                      setShiftEditingState(null);
                      setShiftDropdownVisible(false);
                      shiftInputRef.current?.blur();
                    }
                  }}
                />
                {shiftDropdownVisible && shiftDropdownPosition && (
                  <DropdownMenu
                    items={shiftMenuItems}
                    visible={true}
                    onClose={() => setShiftDropdownVisible(false)}
                    onSelect={async (item) => {
                      if (!scheduleID) return;
                      const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
                      try {
                        await upsertCalDate(scheduleID, {
                          calDateDt: dateStr,
                          calDateBelongScheduleID: scheduleID,
                          calDateShift: item.label,
                        });
                        onScheduleRowsRefresh?.();
                        setShiftEditingState(null);
                        setShiftDropdownVisible(false);
                      } catch (error) {
                        console.error('更新计划日排班失败:', error);
                      }
                    }}
                    position={shiftDropdownPosition}
                    useFixedPosition={true}
                    width={80}
                    selectedValue={todayCalDate?.calDateShift || null}
                  />
                )}
              </>
            );
          })()}
        </div>
        <div className="scheduleDetails-today-Info-right">
          {totalPagesDisplay} {t('schedule.pages')}
        </div>
      </div>
      <div className="scheduleDetails-today-breakdownList">
        <CurrentDateDetailRowsList />
      </div>
      <CurrentDateDetailsElements
        elementRelations={elementRelations}
        todaySceneIds={todaySceneIds}
        breakdownID={breakdownID}
      />
    </div>
  );
};

export default CurrentDateDetails;
